import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Bot, 
  Megaphone, 
  Wallet, 
  Shield, 
  ArrowRight, 
  MessageSquare, 
  Zap, 
  BarChart3,
  Globe,
  Activity,
  Clock,
  CheckCircle2,
  HardDrive
} from 'lucide-react';
import { Badge } from '../components/ui/badge';

// --- STYLED SUB-COMPONENTS ---
const FeatureCard = ({ icon: Icon, title, description, color }) => (
  <div className="group relative p-8 bg-white rounded-[2rem] border border-slate-100 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 overflow-hidden">
    <div className={`absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16 rounded-full opacity-5 group-hover:opacity-10 transition-opacity bg-${color}-600`} />
    <div className={`w-14 h-14 rounded-2xl bg-${color}-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}>
      <Icon className={`w-7 h-7 text-${color}-600`} />
    </div>
    <h3 className="text-xl font-black text-slate-800 mb-3">{title}</h3>
    <p className="text-slate-500 leading-relaxed text-sm font-medium">
      {description}
    </p>
  </div>
);

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-indigo-100 selection:text-indigo-900">
      
      {/* 1. ULTRA-MODERN NAVBAR */}
      <nav className="fixed w-full bg-white/70 backdrop-blur-xl border-b border-slate-100 z-[100]">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200 group-hover:rotate-12 transition-transform">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-black text-xl text-slate-900 tracking-tighter leading-none">Whatsappbot</span>
              <span className="text-[10px] font-bold text-indigo-500 tracking-widest uppercase">.eu</span>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-8">
            <a href="#features" className="text-sm font-bold text-slate-500 hover:text-indigo-600 transition-colors">Features</a>
            <a href="#automation" className="text-sm font-bold text-slate-500 hover:text-indigo-600 transition-colors">AI Engine</a>
            <a href="#safety" className="text-sm font-bold text-slate-500 hover:text-indigo-600 transition-colors">Safety</a>
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard" className="px-6 py-2.5 text-sm font-black text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center gap-2">
                Dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-bold text-slate-600 hover:text-indigo-600 transition-colors">Sign In</Link>
                <Link to="/register" className="px-6 py-2.5 text-sm font-black text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
                  Join Whatsappbot.eu
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* 2. HERO: THE 3D CANVAS */}
      <section className="relative pt-48 pb-32 px-6 overflow-hidden">
        {/* Background 3D Blobs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10">
          <div className="absolute top-20 left-[10%] w-72 h-72 bg-indigo-300/30 rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-[10%] w-96 h-96 bg-blue-300/20 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-black uppercase tracking-widest animate-bounce">
              <Zap className="w-4 h-4" /> 2026 Enterprise Ready
            </div>
            <h1 className="text-6xl md:text-8xl font-black text-slate-900 tracking-tighter leading-[0.9]">
              The AI Core for <span className="text-indigo-600">WhatsApp.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-500 font-medium leading-relaxed max-w-xl mx-auto lg:mx-0">
              Transform your WhatsApp into a high-performance growth machine. AI-powered conversations, bulk automation, and enterprise security—all at <strong>Whatsappbot.eu</strong>
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
              {user ? (
                <Link to="/dashboard" className="w-full sm:w-auto px-10 py-5 text-lg font-black text-white bg-indigo-600 rounded-2xl hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-200 hover:-translate-y-1 flex items-center justify-center gap-3">
                  Back to Dashboard <Activity className="w-5 h-5" />
                </Link>
              ) : (
                <>
                  <Link to="/register" className="w-full sm:w-auto px-10 py-5 text-lg font-black text-white bg-indigo-600 rounded-2xl hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-200 hover:-translate-y-1 flex items-center justify-center gap-3">
                    Start Free <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/login" className="w-full sm:w-auto px-10 py-5 text-lg font-black text-slate-700 bg-white border-2 border-slate-100 rounded-2xl hover:bg-slate-50 transition-all shadow-sm">
                    Live Demo
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* 3D Mockup Graphic Placeholder */}
          <div className="relative group">
            <div className="absolute inset-0 bg-indigo-600 rounded-[3rem] blur-3xl opacity-10 group-hover:opacity-20 transition-opacity" />
            <div className="relative bg-slate-900 rounded-[2.5rem] border-[8px] border-slate-800 shadow-2xl overflow-hidden aspect-video transform rotate-2 group-hover:rotate-0 transition-transform duration-700">
               <div className="absolute top-0 left-0 w-full h-8 bg-slate-800 flex items-center px-4 gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <div className="w-2 h-2 rounded-full bg-green-500" />
               </div>
               <div className="p-12 h-full flex items-center justify-center">
                  <div className="text-center">
                    <Bot className="w-16 h-16 text-indigo-500 mx-auto mb-4 animate-pulse" />
                    <p className="text-slate-400 font-mono text-sm">System: WhatsApp Node Connected</p>
                    <p className="text-indigo-400 font-mono text-xs mt-1">Status: Running AI Inference...</p>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. THE INFRASTRUCTURE STATS */}
      <section className="py-20 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
          <div>
            <p className="text-4xl font-black text-slate-900">2M+</p>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Messages Sent</p>
          </div>
          <div>
            <p className="text-4xl font-black text-slate-900">99.9%</p>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Uptime Core</p>
          </div>
          <div>
            <p className="text-4xl font-black text-slate-900">500ms</p>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">AI Latency</p>
          </div>
          <div>
            <p className="text-4xl font-black text-slate-900">10k+</p>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Active Bots</p>
          </div>
        </div>
      </section>

      {/* 4. DEEP FEATURE GRID (The 3D Cards) */}
      <section id="features" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-20 space-y-4">
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Enterprise Infrastructure for Modern Marketing</h2>
            <p className="text-slate-500 font-medium italic">We didn't just build a tool, we built a secure messaging ecosystem.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <FeatureCard 
              icon={Bot} 
              color="indigo"
              title="LLM Powered Brain" 
              description="Connect your business to Phi-3, GPT-4, or Custom models. Our AI handles support, sales, and FAQs with a 98% human-like accuracy score." 
            />
            <FeatureCard 
              icon={Shield} 
              color="rose"
              title="Anti-Ban Shield" 
              description="Proprietary queueing system with random sleep intervals, browser fingerprinting, and human-like typing simulation to keep your number 100% safe." 
            />
            <FeatureCard 
              icon={Wallet} 
              color="emerald"
              title="Financial Core" 
              description="Built-in wallet with transparent pay-as-you-go billing. No monthly hidden fees—only pay for the messages your business actually sends." 
            />
            <FeatureCard 
              icon={Globe} 
              color="blue"
              title="Multi-Device Sync" 
              description="Manage 10+ WhatsApp numbers from a single admin panel. Perfect for agencies managing multiple client accounts simultaneously." 
            />
            <FeatureCard 
              icon={HardDrive} 
              color="amber"
              title="Media Repository" 
              description="Full support for PDF, Images, Video, and Voice notes. All campaign assets are processed through our optimized CDN for instant delivery." 
            />
            <FeatureCard 
              icon={BarChart3} 
              color="purple"
              title="Real-time Analytics" 
              description="Monitor every token, every credit, and every interaction. Detailed delivery reports ensure your campaigns are reaching your target audience." 
            />
          </div>
        </div>
      </section>

      {/* 5. THE 'HOW IT WORKS' 3-STEP FLOW */}
      <section className="py-32 bg-slate-900 text-white rounded-[4rem] mx-4 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-24">
            <Badge className="bg-indigo-500/20 text-indigo-400 border-none mb-4">The Process</Badge>
            <h2 className="text-5xl font-black tracking-tight">Launch in 180 Seconds.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-16 relative">
            {/* Step 1 */}
            <div className="relative z-10">
              <div className="text-8xl font-black text-white/5 absolute -top-12 -left-4">01</div>
              <div className="w-16 h-16 rounded-2xl bg-indigo-600 flex items-center justify-center mb-8 shadow-xl shadow-indigo-500/20">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold mb-4">Initialize Account</h4>
              <p className="text-slate-400 leading-relaxed font-medium">Create your ID at Whatsappbot.eu. Every new user gets initial credits to test our AI and Bulk engines immediately.</p>
            </div>
            {/* Step 2 */}
            <div className="relative z-10">
              <div className="text-8xl font-black text-white/5 absolute -top-12 -left-4">02</div>
              <div className="w-16 h-16 rounded-2xl bg-indigo-600 flex items-center justify-center mb-8 shadow-xl shadow-indigo-500/20">
                <Zap className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold mb-4">Scan & Sync</h4>
              <p className="text-slate-400 leading-relaxed font-medium">Open your WhatsApp and scan our secure QR code. Our Puppeteer clusters handle the connection encryption instantly.</p>
            </div>
            {/* Step 3 */}
            <div className="relative z-10">
              <div className="text-8xl font-black text-white/5 absolute -top-12 -left-4">03</div>
              <div className="w-16 h-16 rounded-2xl bg-indigo-600 flex items-center justify-center mb-8 shadow-xl shadow-indigo-500/20">
                <Activity className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold mb-4">Go Live</h4>
              <p className="text-slate-400 leading-relaxed font-medium">Deploy your AI prompt or start your first campaign. Sit back and watch your wallet generate value in real-time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. CALL TO ACTION (CTA) SECTION */}
      <section className="py-40 px-6">
        <div className="max-w-5xl mx-auto bg-indigo-600 rounded-[3rem] p-12 md:p-24 text-center text-white relative shadow-2xl shadow-indigo-200 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full -ml-32 -mb-32" />
          
          <div className="relative z-10 space-y-8">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter">Scale your growth with the Whatsappbot.eu core.</h2>
            <p className="text-indigo-100 text-lg md:text-xl font-medium max-w-2xl mx-auto">
              Ready to automate? Join the platform built for 2026. Get started with zero setup fees today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-6">
              {user ? (
                <Link to="/dashboard" className="px-12 py-5 text-xl font-black text-indigo-600 bg-white rounded-2xl hover:bg-slate-50 transition-all shadow-xl hover:-translate-y-1">
                  Return to Dashboard
                </Link>
              ) : (
                <Link to="/register" className="px-12 py-5 text-xl font-black text-indigo-600 bg-white rounded-2xl hover:bg-slate-50 transition-all shadow-xl hover:-translate-y-1">
                  Create My ID Now
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 7. THE PROFESSIONAL FOOTER */}
      <footer className="bg-white py-20 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-20">
            <div className="col-span-2 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <span className="font-black text-xl text-slate-900 tracking-tighter">Whatsappbot</span>
              </div>
              <p className="text-slate-500 font-medium max-w-xs leading-relaxed">
                The leading enterprise WhatsApp automation core. Secure, Scalable, and AI-Powered for the next generation of marketing.
              </p>
            </div>
            <div>
              <h5 className="font-black text-slate-900 mb-6 uppercase tracking-widest text-[10px]">Product</h5>
              <ul className="space-y-4 text-sm font-bold text-slate-500">
                <li><a href="#features" className="hover:text-indigo-600 transition-colors">Core Features</a></li>
                <li><a href="#safety" className="hover:text-indigo-600 transition-colors">Safety Protocols</a></li>
                <li><Link to="/register" className="hover:text-indigo-600 transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-black text-slate-900 mb-6 uppercase tracking-widest text-[10px]">Developer</h5>
              <ul className="space-y-4 text-sm font-bold text-slate-500">
                <li><a href="#" className="hover:text-indigo-600 transition-colors">API Reference</a></li>
                <li><a href="#" className="hover:text-indigo-600 transition-colors">System Status</a></li>
                <li><a href="#" className="hover:text-indigo-600 transition-colors">Whatsappbot.eu Domain</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-12 border-t border-slate-50 flex flex-col md:flex-row items-center justify-between gap-6 text-sm font-bold text-slate-400">
            <p>© 2026 Whatsappbot SaaS. Proudly developed by Yuvraj Kohli.</p>
            <div className="flex gap-8">
              <a href="#" className="hover:text-slate-900">Privacy Policy</a>
              <a href="#" className="hover:text-slate-900">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}